/* eslint-disable no-unused-vars */
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, NavLink } from "react-router-dom";
import TextInput from "./TextInput";
import CustomButton from "./CustomButton";
import { useForm } from "react-hook-form";
import { BsMoon, BsPersonPlus, BsSun, BsSearch } from "react-icons/bs";
import { IoMdNotificationsOutline } from "react-icons/io";
import { SetTheme } from "../redux/theme";
import { Logout } from "../redux/userSlice";
import { fetchPosts } from "../utils";
import { BiHome } from "react-icons/bi";
import { LiaUserFriendsSolid } from "react-icons/lia";
import { NoProfile } from "../assets";
import metaicon from "../assets/metaicon.png";
import { HiMenu, HiX } from "react-icons/hi";

const TopBar = () => {
  const { theme } = useSelector((state) => state.theme);
  const { user } = useSelector((state) => state.user);
  const dispatch = useDispatch();

  const { register, handleSubmit } = useForm();

  const [aiOpen, setAiOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleSearch = async (data) => {
    await fetchPosts(user.token, dispatch, "", data);
  };

  const handleTheme = () => {
    const themeValue = theme === "light" ? "dark" : "light";
    dispatch(SetTheme(themeValue));
  };

  const handleOpenMeta = () => {
    window.open("https://www.meta.ai", "_blank", "width=1200,height=800");
    setAiOpen(false);
  };

  // ⬇️ If no user, return nothing
  if (!user) return null;

  return (
    <>
      <div className="topbar w-full flex items-center justify-between py-2 px-4 bg-primary relative">
        {/* Logo */}
        <Link to="/" className="flex gap-2 items-center">
          <div className="p-1 md:p-2 bg-[#1877F2] rounded text-white">SG</div>
        </Link>

        {/* Search Bar */}
        <form
          className="hidden md:flex ml-6"
          onSubmit={handleSubmit(handleSearch)}
        >
          <TextInput
            placeholder="Search..."
            styles="w-[12rem] lg:w-[14rem] rounded-l-full py-2"
            register={register("search")}
          />
          <CustomButton
            title={<BsSearch size={18} />}
            type="submit"
            containerStyles="bg-[#1877F2] text-white px-3 py-2 border-[#1877F2] mt-2 rounded-r-full"
          />
        </form>

        {/* CENTER ICONS */}
        <div className="flex items-center justify-center gap-6 sm:gap-8 md:gap-12 lg:gap-24 flex-1">
          <NavLink to="/" className={({ isActive }) => (isActive ? "text-[#1877F2]" : "text-ascent-2")}>
            <BiHome size={25} />
          </NavLink>
          <NavLink to="/friend-request" className={({ isActive }) => (isActive ? "text-[#1877F2]" : "text-ascent-2")}>
            <LiaUserFriendsSolid size={25} />
          </NavLink>
          <NavLink to="/suggested-friends" className={({ isActive }) => (isActive ? "text-[#1877F2]" : "text-ascent-2")}>
            <BsPersonPlus size={25} />
          </NavLink>
          <NavLink to="/notifications" className={({ isActive }) => (isActive ? "text-[#1877F2]" : "text-ascent-2")}>
            <IoMdNotificationsOutline size={25} />
          </NavLink>
        </div>

        {/* RIGHT ICONS */}
        <div className="hidden md:flex gap-4 items-center text-ascent-1 text-lg">
          <NavLink
            to={`/profile/${user?._id}`}
            className={({ isActive }) =>
              `text-ascent-2 ${isActive ? "ring-2 ring-[#1877F2] rounded-full" : ""}`
            }
          >
            <img
              src={user?.profileUrl ?? NoProfile}
              alt={user?.email}
              className="w-9 h-9 md:w-10 md:h-10 object-cover rounded-full"
            />
          </NavLink>

          <button onClick={handleTheme} className="text-xl">
            {theme === "light" ? <BsMoon /> : <BsSun />}
          </button>

          <button onClick={() => setAiOpen(true)} title="Ask Meta AI">
            <img src={metaicon} alt="Meta AI" className="w-7 h-7 md:w-8 md:h-8" />
          </button>

          <CustomButton
            onClick={() => dispatch(Logout())}
            containerStyles="flex items-center justify-center text-xs bg-[#1877F2] text-white px-3 md:px-4 py-1 md:py-2 rounded-full"
            title={<span>Log Out</span>}
          />
        </div>

        {/* Hamburger Menu */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="md:hidden text-2xl text-ascent-1 ml-4"
        >
          {menuOpen ? <HiX /> : <HiMenu />}
        </button>

        {/* Mobile Dropdown */}
        {menuOpen && (
  <div className="absolute top-full right-4 mt-2 w-48 bg-secondary shadow-lg rounded-lg flex flex-col p-3 z-50 md:hidden text-ascent-1 dark:text-white">
    {/* Profile */}
    <NavLink
      to={`/profile/${user?._id}`}
      onClick={() => setMenuOpen(false)}
      className="flex items-center gap-2 p-2 hover:bg-primary rounded-md"
    >
      <img
        src={user?.profileUrl ?? NoProfile}
        alt={user?.email}
        className="w-8 h-8 object-cover rounded-full"
      />
      <span>Profile</span>
    </NavLink>

    {/* Theme Toggle */}
    <button
      onClick={() => {
        handleTheme();
        setMenuOpen(false);
      }}
      className="flex items-center gap-2 p-2 hover:bg-primary rounded-md"
    >
      {theme === "light" ? <BsMoon /> : <BsSun />}
      <span>Theme</span>
    </button>

    {/* Meta AI */}
    <button
      onClick={() => setAiOpen(true)}
      title="Ask Meta AI"
      className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 flex items-center justify-start"
    >
      <img
        src={metaicon}
        alt="Meta AI"
        className="w-7 h-7 md:w-8 md:h-8"
      />
    </button>

    {/* Logout */}
    <button
      onClick={() => {
        dispatch(Logout());
        setMenuOpen(false);
      }}
      className="flex items-center gap-2 p-2 hover:bg-primary rounded-md text-red-500 dark:text-red-400"
    >
      <span>Logout</span>
    </button>
  </div>
)}

      </div>

      {/* AI Assistant Modal */}
      {aiOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-2xl shadow-lg max-w-md w-full text-center">
            <h2 className="text-xl font-semibold mb-4">Open Meta AI?</h2>
            <p className="mb-6">We’ll open Meta AI in a new window.</p>
            <div className="flex justify-center gap-4">
              <button onClick={handleOpenMeta} className="bg-blue-600 px-4 py-2 rounded-lg text-white">
                Open
              </button>
              <button onClick={() => setAiOpen(false)} className="bg-gray-300 px-4 py-2 rounded-lg">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};


export default TopBar;
